import {FC, useMemo} from 'react';
import {Col, Row} from 'react-bootstrap';
import {Formik, Form, FormikHelpers} from 'formik';
import * as Yup from 'yup';
import {FormPageHeader, FormPageFooter} from "@components/falcon/common/forms";
import {RefRewardBaseInfoBlock, RefRewardContentBlock} from "../forms";
import {RefRewardFormValues} from "../forms/refRewardBaseInfoBlock";
import {RefRewardType} from "@interfaces/consumer/refs";

interface RefRewardFormProps {
    initialValues: RefRewardFormValues;
    onSubmit: (values: RefRewardFormValues, actions: FormikHelpers<RefRewardFormValues>) => Promise<any>;
    editForm?: boolean;
    title: string;
    cancelUrl: string;
}

const RefRewardForm: FC<RefRewardFormProps> = ({
    initialValues,
    onSubmit,
    editForm,
    title,
    cancelUrl
}) => {

    const validationSchema = useMemo(() => Yup.object<RefRewardFormValues>({
        name: Yup.string()
            .max(256)
            .required(),
        order: Yup.number()
            .min(-65000)
            .max(65000)
            .required(),
        isActive: Yup.boolean()
            .required(),
        type: Yup.mixed<RefRewardType>()
            .oneOf([RefRewardType.Balance, RefRewardType.Item, RefRewardType.Percent])
            .required(),
        balance: Yup.number()
            .when("type", {
                is: RefRewardType.Balance,
                then: schema => schema
                    .min(1)
                    .max(1000000)
                    .required()
            }),
        itemId: Yup.string()
            .when('type', {
                is: RefRewardType.Item,
                then: schema => schema
                    .required()
            }),
        count: Yup.number()
            .when('type', {
                is: RefRewardType.Item,
                then: schema => schema
                    .min(1)
                    .max(2000000000)
                    .required()
            }),
        percent: Yup.number()
            .when('type', {
                is: RefRewardType.Percent,
                then: schema => schema
                    .min(0)
                    .max(100)
                    .required()
            }),
        minSum: Yup.number()
            .when('type', {
                is: RefRewardType.Percent,
                then: schema => schema
                    .min(0)
                    .max(1000000)
                    .required()
            }),
        maxSum: Yup.number()
            .when('type', {
                is: RefRewardType.Percent,
                then: schema => schema
                    .min(0)
                    .max(1000000)
                    .required()
            })
    }), []);

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            enableReinitialize
        >
            <Form>
                <Row className="g-3">
                    <Col lg={12}>
                        <FormPageHeader title={title} />
                    </Col>
                    <Col lg={8}>
                        <RefRewardBaseInfoBlock editForm={editForm} />
                    </Col>
                    <Col lg={4}>
                        <RefRewardContentBlock />
                    </Col>
                    <Col>
                        <FormPageFooter cancelUrl={cancelUrl} />
                    </Col>
                </Row>
            </Form>
        </Formik>
    );
};

export default RefRewardForm;
